/**
 * Store de sucursal activa. Persiste en localStorage para que el manager
 * no tenga que elegir cada vez que recarga la página.
 *
 * Si el manager tiene varias sucursales, elige una en /sucursales.
 * Si tiene una sola, se asigna automáticamente al login.
 */

import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { SucursalResumen } from '@carwash-pos/shared';
import { operacionesApi, type OperacionSucursal } from '@/lib/api-pos';

const SUCURSAL_STORAGE_KEY = 'cwpos_active_sucursal_id';

export const useSucursalStore = defineStore('sucursal', () => {
  const activeSucursalId = ref<number | null>(
    (() => {
      const stored = localStorage.getItem(SUCURSAL_STORAGE_KEY);
      return stored ? Number(stored) : null;
    })(),
  );
  const disponibles = ref<SucursalResumen[]>([]);
  const operacion = ref<OperacionSucursal | null>(null);

  function set(id: number): void {
    activeSucursalId.value = id;
    localStorage.setItem(SUCURSAL_STORAGE_KEY, String(id));
  }

  function clear(): void {
    activeSucursalId.value = null;
    localStorage.removeItem(SUCURSAL_STORAGE_KEY);
  }

  function setOperacion(value: OperacionSucursal | null): void {
    operacion.value = value;
    if (value) set(value.sucursal_id);
  }

  async function recuperarOperacion(): Promise<void> {
    const res = await operacionesApi.actual();
    setOperacion(res.operacion);
  }

  function setDisponibles(sucursales: SucursalResumen[]): void {
    disponibles.value = sucursales;
  }

  function reset(): void {
    clear();
    disponibles.value = [];
    operacion.value = null;
  }

  return {
    activeSucursalId,
    disponibles,
    operacion,
    set,
    clear,
    setOperacion,
    recuperarOperacion,
    setDisponibles,
    reset,
  };
});
